import { Skeleton } from '@material-ui/lab';
import React, { ReactElement, useState } from 'react';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAuth from 'src/hooks/useAuth';
import { CodeusAccountConfig } from 'src/types/dataTypes';
import AccountRegionSwitcher from 'src/components/AccountRegionSwitcher';
import FEATURES from 'src/featureMap';
import { useApiRequest } from 'src/hooks/useApi';
import {
	Alert,
	Button,
	Dialog,
	DialogContent,
	DialogActions,
	DialogTitle,
	Typography,
	Checkbox,
	BDSProvider,
	FormControlLabel,
	Grid,
} from '@barracuda-internal/bds-core-v2';
import withAnalytics from 'src/lib/withAnalytics';
import useSettingsStyles from './styles/settings';
interface Props {
	noSaveButton?: boolean;
	onChange?: (region: string) => void;
}

const locationMap = {
	US: {
		icon: '🇺🇸',
		text: 'United States',
	},
	CA: {
		icon: '🇨🇦',
		text: 'Canada',
	},
	NL: {
		icon: '🇪🇺',
		text: 'European Union',
	},
	GB: {
		icon: '🇬🇧',
		text: 'Great Britain',
	},
	JP: {
		icon: '🇯🇵',
		text: 'Japan',
	},
	AU: {
		icon: '🇦🇺',
		text: 'Australia',
	},
	IN: {
		icon: '🇮🇳',
		text: 'India',
	},
};

function AccountRegion({ onChange }: Props): ReactElement {
	const { api } = useAppToolchain();
	const { auth } = useAuth();
	const customClasses = useSettingsStyles();
	const [selectedRegion, setSelectedRegion] = useState<string>();
	const [warnWin, setWarnWin] = useState(false);
	const [doUpdate, setDoUpdate] = useState(false);
	const [visible, setVisible] = useState(false);
	const [acceptWarn, setAcceptWarn] = useState(false);
	const handleChangeRegion = () => {
		setDoUpdate(true);
		setWarnWin(false);
		setVisible(false);
		setAcceptWarn(false);
	};
	const [updateStatus, res, err] = useApiRequest(
		doUpdate &&
			((signal) => {
				return api
					.saveAccountConfig(
						auth.accountDetails.account.id,
						[
							{
								path: '/storageRegion',
								op: 'replace',
								value: selectedRegion,
							},
						],
						{ signal },
					)
					.then((res) => {
						setConfig(res);
						setSelectedRegion(res.storageRegion);
						onChange && onChange(res.storageRegion);
						if (err) return;
					});
			}),
		[doUpdate],
		() => {
			setDoUpdate(false);
		},
	);

	const handleRegionChange = withAnalytics(
		FEATURES.onboardingDataLocationChange,
		(region) => setSelectedRegion(region),
		(region) => {
			return { region };
		},
	);

	const onClose = () => {
		setWarnWin(false);
	};

	const showDropdown = () => {
		setVisible(true);
	};
	const hideDropdown = () => {
		setVisible(false);
	};
	const [config, setConfig] = useState<CodeusAccountConfig>();

	const [configStatus] = useApiRequest(() => {
		if (!auth) return;

		return api
			.getAccountConfig(auth?.accountDetails.account.id)
			.then((res) => {
				setConfig(res);
				setSelectedRegion(res.storageRegion);
				onChange && onChange(res.storageRegion);
			});
	}, [auth]);

	if (!auth || !config) {
		return <Skeleton height={50} animation="wave" />;
	}

	return (
		<BDSProvider>
			<Dialog open={warnWin} onClose={onClose}>
				<Grid item xs={11} style={{ marginLeft: '16px' }}>
					<DialogTitle style={{ marginTop: '16px' }}>
						<Typography variant="h2">
							Change Data Storage location ?
						</Typography>
					</DialogTitle>

					<DialogContent>
						<Alert severity="info" style={{ marginBottom: '16px' }}>
							All new data from now, will go to the new location
							and all historical data will be moved over as soon
							as possible.
						</Alert>
						<FormControlLabel
							style={{ marginLeft: '16px', marginRight: '16px' }}
							control={
								<Checkbox
									checked={acceptWarn}
									onChange={(e, checked) =>
										setAcceptWarn(checked)
									}
									style={{ marginRight: '8px' }}
								/>
							}
							label="Confirm"
						/>
					</DialogContent>
					<DialogActions>
						<Button
							isLoading={updateStatus === 'RUNNING'}
							bdsType="interactiveSubtle"
							onClick={() => {
								setWarnWin(false);
								setAcceptWarn(false);
							}}
						>
							Cancel
						</Button>
						<Button
							disabled={!acceptWarn}
							bdsType="interactiveEmphasis"
							onClick={handleChangeRegion}
							isLoading={updateStatus === 'RUNNING'}
						>
							Save
						</Button>
					</DialogActions>
				</Grid>
			</Dialog>
			<div className="mb_4">
				<Typography variant="h3" color="textPrimary">
					Data Storage Region
					{err ? (
						<Alert color="error">
							An error occurred while saving the data location.
							Please reload the page and try again.
						</Alert>
					) : null}
				</Typography>
				<div
					style={{
						paddingTop: '24px',
						paddingBottom: '24px',
						marginLeft: '16px',
					}}
				>
					<Typography color="textPrimary">
						{locationMap[config.storageRegion]
							? locationMap[config.storageRegion].icon
							: ''}{' '}
						&nbsp;&nbsp; Data is stored in{' '}
						{locationMap[config.storageRegion]
							? locationMap[config.storageRegion].text
							: config.storageRegion}
						<Button
							variant="outlined"
							color="secondary"
							onClick={() => {
								showDropdown();
							}}
							className={customClasses['l_15']}
						>
							Edit
						</Button>
						<Typography>
							<br></br>
							Data Inspector stores your organization's and tenant
							information according to your region, ensuring
							compliance with storage-related regulatory policies.
						</Typography>
					</Typography>
				</div>

				<Grid
					container
					className={customClasses['data-storage-dropdown-container']}
				>
					{visible && (
						<Grid item xs={12}>
							<AccountRegionSwitcher
								onChange={handleRegionChange}
							></AccountRegionSwitcher>
							<Button
								bdsType="interactiveSubtle"
								onClick={() => {
									hideDropdown();
								}}
							>
								Cancel
							</Button>
							<Button
								bdsType="interactiveEmphasis"
								disabled={
									selectedRegion === config.storageRegion
								}
								onClick={() => {
									setWarnWin(true);
								}}
								style={{ marginLeft: '24px' }}
							>
								Update
							</Button>
						</Grid>
					)}
				</Grid>
			</div>
		</BDSProvider>
	);
}

export default AccountRegion;
